import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DialogControlService } from '@shared/services/dialog-control.service';
import { CancelRefundModalComponent } from '../cancel-refund-modal/cancel-refund-modal.component';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { BookingTypeDetails, CancelType } from '@core/models/booking.model';
import { CANCEL_BOOKING_CONFIRMATION_MODAL, cancellationBookingValidationMessage, cancellationReasons, USER_ROLES } from '@shared/constant/shared.constant';
import { Dropdown } from 'primeng/dropdown';
import { EncryptionStorageService } from '@core/services/encryption-storage.service';
interface TimeOption {
  name: string;
  value: any;
}

@Component({
  selector: 'app-cancel-option-modal',
  templateUrl: './cancel-option-modal.component.html',
  styleUrls: ['./cancel-option-modal.component.scss'],
})
export class CancelOptionModalComponent implements OnInit {
  @ViewChild('schedileTime') schedileTime!: Dropdown;
  cancelOptionForm: FormGroup;
  bookingType: number;
  bookingId: string;
  bookingTypeDetails = BookingTypeDetails;
  cancelBookingTitle = CANCEL_BOOKING_CONFIRMATION_MODAL;
  cancelType = CancelType;
  showError: boolean = false;
  paymentStatus: number;
  showRadioButtonsForPendingCase: boolean = false;
  onlyShowSubscriptionCancelOption: boolean = false;
  isScheduleCancelOption: boolean = false;
  timeOptions: TimeOption[] = [];
  minSelectableDate: Date = new Date(new Date().setHours(0, 0, 0, 0));
  bookingEndDate:string = '';
  maxSelectableDate: Date | null = null;
  isScheduleDateTimeValid: boolean= false;
  scheduleDateTimeValue: string = ''
  isSubmitButtonClicked: boolean = false;
  isScheduleCancelBookingMessage: boolean = false;
  roleName: string = '';
  userRoles = USER_ROLES;
  timeZone: string = '';
  bookingScheduledDate:string = '';
  validationErrorMessage:string =  '';
  cancellationReasons: { label: string, value: string}[] = cancellationReasons.map(reason => ({
    label: reason,
    value: reason
  }));

  constructor(
    private readonly fb: FormBuilder,
    private readonly dialogControlService: DialogControlService,
    private readonly ref: DynamicDialogRef,
    public readonly config: DynamicDialogConfig,
    private readonly encryptStorage: EncryptionStorageService
  ) {}

  ngOnInit(): void {
    this.roleName = this.encryptStorage.getItem('roleName') || '';
    this.bookingId = this.config?.data?.bookingId;
    this.bookingType = this.config?.data?.bookingType;
    this.paymentStatus = this.config?.data?.paymentStatus;
    this.bookingEndDate = this.config?.data?.endDate || '';
    this.timeZone = this.config?.data?.timeZone || '';
    this.bookingScheduledDate = this.config?.data?.bookingScheduledDate
    if (this.bookingType === 0 && this.bookingEndDate) {
      this.maxSelectableDate = new Date(this.bookingEndDate);
    }
    this.showRadioButtonsForPendingCase = [3, 5, 6, 8, 10, 11].includes(this.paymentStatus);
    this.onlyShowSubscriptionCancelOption = [4].includes(this.paymentStatus);
    this.cancelOptionForm = this.fb.group({
      cancelOption: [null],
      cancellationReason: [null, Validators.required],
      additionalComments: [''],
    });
  }

  schdeuleDateTimeData(event) {
    this.isScheduleDateTimeValid = event?.isScheduleDateTime;
    this.scheduleDateTimeValue = event?.dateTime;
    this.isSubmitButtonClicked = event?.isSubmitButtonClicked;
  }

  onSubmit(): void {
    this.isSubmitButtonClicked = true;
     // Mark all fields as touched to show validation errors
     this.cancelOptionForm.markAllAsTouched();
     const validationResult = this.checkCancelOptionFormValidation(this.cancelOptionForm);
     this.showError = !validationResult.isValid;
     this.validationErrorMessage = validationResult.message;
 
     if (!validationResult.isValid) {
      return;
    }

    if (this.isScheduleDateTimeValid) {
      return;
    }

    this.nextPopup();
  }
 
  checkCancelOptionFormValidation(formGroup: FormGroup): { isValid: boolean; message: string } {
    const cancelOption = formGroup.get('cancelOption');
    const cancellationReason = formGroup.get('cancellationReason');
    const additionalComments = formGroup.get('additionalComments');

    if (!cancelOption?.value) {
      return { isValid: false, message: cancellationBookingValidationMessage.radioButtonValidation };
    }

    if (cancellationReason?.hasError('required') && this.bookingType != 0) {
      return { isValid: false, message: cancellationBookingValidationMessage.reasonDropDownValidation };
    }

    const selectedReason = cancellationReason?.value;

    if (this.isReasonRequiringExplanation(selectedReason) && !additionalComments?.value?.trim() && this.bookingType != 0) {
      return { isValid: false, message: cancellationBookingValidationMessage.additionalCommentValidation };
    }

    // Add more validations as needed
    return { isValid: true, message: '' };
  }

  setCancelOption(value: any): void {
    this.isScheduleCancelOption = true;
    this.cancelOptionForm.get('cancelOption')?.setValue(value);
    this.showError = false;
  }

  nextPopup(): void {
    this.closeDialog();
    this.dialogControlService.openDialog(CancelRefundModalComponent, {
      styleClass: 'initiate-booking-models',
      data: {
        bookingId: this.bookingId,
        bookingType: this.bookingType,
        cancelType: this.cancelOptionForm.value.cancelOption,
        scheduledCancelDate: this.scheduleDateTimeValue,
        showRadioButtonsForPendingCase: this.showRadioButtonsForPendingCase,
        cancellationReason:this.cancelOptionForm.value.cancellationReason,
        additionalComments:this.cancelOptionForm.value.additionalComments
      },
    });
  }

  handleConditionalValidation(selectedValue: string): void {
    const commentsControl = this.cancelOptionForm.get('additionalComments');

    if (this.isReasonRequiringExplanation(selectedValue)) {
      commentsControl?.setValidators([Validators.required]);
      commentsControl.markAsTouched();
    } else {
      commentsControl?.clearValidators();
    }

    commentsControl?.updateValueAndValidity();
  }

  cancellationReasonsOnChanges(_event) {
    this.checkCancelOptionFormValidation(this.cancelOptionForm);
    this.showError = false;
  }

  isReasonRequiringExplanation(reason: string): boolean {
    return (
      reason === 'Customer Service Concern' ||
      reason === 'Other'
    );
  }

  closeDialog() {
    this.ref.close();
  }
}
